/**
 * TestNewQuotePage4 — protótipo VISUAL da tela "Novo orçamento" (/quotes/new).
 *
 * Escrito do ZERO, sem herdar nada do layout das páginas de teste 2 e 3: aqui a
 * linguagem visual é "hero + folha" — um cabeçalho verde sangrado com o total ao
 * vivo, um cartão flutuante do cliente sobrepondo a curva, atalhos em ladrilho e
 * os itens desenhados como BILHETE (coluna de preço separada por picote), em vez
 * dos cards brutalistas do formulário real.
 *
 * O conteúdo é a lista COMPLETA de funcionalidades e botões de /quotes/new (ver
 * o inventário no fim do arquivo). É um sandbox de layout: estado local só, sem
 * Supabase, sem fila offline e sem gravar nada — a rota é registrada apenas em
 * DEV, junto das outras páginas de teste.
 *
 * As cores são as do app (token `--primary`, o verde 152 69% 42%), com âmbar só
 * para PRO (CLAUDE.md #20/#24) e o azul #2f6fae reservado a serviço/informação.
 * A conta do total NÃO é reimplementada: usa `computeQuoteTotals` e o
 * `QuoteTotalsBreakdown` de produção, para o protótipo não mentir sobre a ordem
 * de aplicação de desconto e frete (CLAUDE.md #69).
 */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  ArrowDown,
  ArrowLeft,
  ArrowUp,
  GripVertical,
  Barcode,
  Calendar,
  Camera,
  ChevronDown,
  Contact,
  Download,
  Eye,
  FileText,
  Hash,
  Image as ImageIcon,
  Link2,
  Loader2,
  MapPin,
  Minus,
  Package,
  Pencil,
  Phone,
  Plus,
  RefreshCw,
  Save,
  Search,
  ShoppingBag,
  Sparkles,
  Trash2,
  User,
  UserPlus,
  WifiOff,
  Wrench,
  X,
} from 'lucide-react';
import { MainLayout } from '@/components/layout/MainLayout';
import { Button } from '@/components/ui/button';
import { Button3D } from '@/components/ui/button-3d';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { QuoteTotalsBreakdown } from '@/components/quotes/QuoteTotalsBreakdown';
import { computeQuoteTotals, quoteItemLineTotal } from '@/lib/quoteTotals';
import { maskBRLInput, numberToMaskedBRL, parseBRLInput } from '@/lib/currencyInput';
import { QUOTE_PAYMENT_METHODS } from '@/lib/quotePaymentInfo';
import { PAYMENT_METHOD_LABELS } from '@/lib/financeLabels';
import {
  WARRANTY_UNITS,
  formatStructuredWarranty,
  suggestWarranty,
  type WarrantyUnit,
} from '@/lib/quoteWarranty';
import { unitGroupsIncluding } from '@/lib/units';
import { getCorrelacaoByLC116, searchServicos } from '@/lib/fiscalData';
import { useNetworkStatus } from '@/hooks/useNetworkStatus';
import { WhatsAppIcon } from '@/components/icons/WhatsAppIcon';
import {
  detectDocumentKind,
  formatCep,
  formatDocument,
  isValidDocument,
  onlyDigits,
} from '@/lib/documentUtils';
import { lookupCep } from '@/lib/cepLookup';
import { createUuid } from '@/lib/uuid';
import { cn } from '@/lib/utils';
import type { PaymentMethod } from '@/types/database';
import { toast } from 'sonner';

/* -------------------------------------------------------------------------- */
/* Vocabulários da tela (espelham o formulário real)                            */
/* -------------------------------------------------------------------------- */

type QuoteStatus = 'pending' | 'approved' | 'completed' | 'cancelled';

const STATUS_OPTIONS: Array<{ value: QuoteStatus; label: string; dot: string }> = [
  { value: 'pending', label: 'Pendente', dot: 'bg-status-pending' },
  { value: 'approved', label: 'Aprovado', dot: 'bg-status-approved' },
  { value: 'completed', label: 'Concluído', dot: 'bg-status-completed' },
  { value: 'cancelled', label: 'Cancelado', dot: 'bg-status-cancelled' },
];

const DOCUMENT_TYPE_GROUPS = [
  {
    label: 'Antes do serviço',
    options: [
      { value: 'orcamento', label: 'Orçamento' },
      { value: 'proposta_comercial', label: 'Proposta comercial' },
      { value: 'cotacao', label: 'Cotação' },
      { value: 'estimativa', label: 'Estimativa' },
    ],
  },
  {
    label: 'Durante o serviço',
    options: [
      { value: 'pedido', label: 'Pedido de venda' },
      { value: 'ordem_servico', label: 'Ordem de serviço' },
      { value: 'plano_trabalho', label: 'Plano de trabalho' },
    ],
  },
  {
    label: 'Depois do serviço',
    options: [
      { value: 'recibo', label: 'Recibo' },
      { value: 'comprovante', label: 'Comprovante' },
    ],
  },
] as const;

const DOCUMENT_TYPE_LABELS: Record<string, string> = Object.fromEntries(
  DOCUMENT_TYPE_GROUPS.flatMap((g) => g.options.map((o) => [o.value, o.label])),
);

const PAYMENT_TERMS_SUGGESTIONS = [
  'À vista',
  '50% na entrada e 50% na entrega',
  'Até 3x sem juros',
  '30 dias após a entrega',
];

const PDF_TEMPLATES = [
  { value: 'classico', label: 'Clássico', pro: false },
  { value: 'moderno', label: 'Moderno', pro: true },
  { value: 'minimalista', label: 'Minimalista', pro: true },
  { value: 'botanico', label: 'Botânico', pro: true },
];

/** Catálogo falso — só para o diálogo "Do catálogo" ter o que listar. */
const MOCK_CATALOG = [
  { name: 'Instalação de tomada', price: 85, type: 'service' as const, unit: 'un' },
  { name: 'Ponto de iluminação', price: 120, type: 'service' as const, unit: 'un' },
  { name: 'Cabo flexível 2,5mm', price: 4.9, type: 'product' as const, unit: 'm' },
  { name: 'Disjuntor 20A', price: 28.5, type: 'product' as const, unit: 'un' },
  { name: 'Visita técnica', price: 150, type: 'service' as const, unit: 'vis' },
];

/** Clientes falsos para o seletor. */
const MOCK_CLIENTS = [
  { id: 'c1', name: 'Ana Paula Ribeiro', phone: '(11) 98877-1122', address: 'Rua das Acácias, 120' },
  { id: 'c2', name: 'Condomínio Vila Nova', phone: '(11) 3344-9080', address: 'Av. Central, 900' },
  { id: 'c3', name: 'Marcos Vinícius Lima', phone: '(21) 99654-3311', address: 'Trav. do Porto, 45' },
];

/* -------------------------------------------------------------------------- */
/* Tipos locais                                                                */
/* -------------------------------------------------------------------------- */

interface MockItem {
  id: string;
  name: string;
  description: string;
  itemType: 'service' | 'product';
  quantity: number;
  unit_price: number;
  discount: number;
  discount_type: 'value' | 'percent';
  unit: string;
  photos: string[];
  saveToCatalog: boolean;
  cost: number | null;
  category: string;
  sku: string;
  barcode: string;
  trackStock: boolean;
  minStock: string;
  fromCatalog: boolean;
  /** Item da LC 116 (serviço) — alimenta a NFS-e no app real. */
  lc116: string;
  /** Alíquota de ISS em % (serviço). */
  issAliquota: string;
}

interface MockClient {
  id: string;
  name: string;
  phone: string;
  address: string;
}

/** Foto geral do documento — título e descrição saem no PDF junto da imagem. */
interface GeneralPhoto {
  id: string;
  title: string;
  description: string;
}

const emptyItem = (itemType: 'service' | 'product'): MockItem => ({
  id: createUuid(),
  name: '',
  description: '',
  itemType,
  quantity: 1,
  unit_price: 0,
  discount: 0,
  discount_type: 'value',
  unit: itemType === 'product' ? 'un' : 'srv',
  photos: [],
  saveToCatalog: false,
  cost: null,
  category: '',
  sku: '',
  barcode: '',
  trackStock: false,
  minStock: '',
  fromCatalog: false,
  lc116: '',
  issAliquota: '',
});

/**
 * Ponteiro GROSSO (dedo) × fino (mouse) — é isso que decide a forma de reordenar,
 * não o tamanho da tela: um notebook com tela estreita continua tendo mouse, e um
 * tablet largo continua sendo dedo. Arrastar com o dedo numa página que rola
 * verticalmente disputa com o próprio scroll; por isso o toque ganha SETAS, e o
 * mouse ganha a alça de arrasto.
 */
function useCoarsePointer() {
  const [coarse, setCoarse] = useState(
    () => Capacitor.isNativePlatform() || window.matchMedia('(pointer: coarse)').matches,
  );
  useEffect(() => {
    if (Capacitor.isNativePlatform()) return;
    const mq = window.matchMedia('(pointer: coarse)');
    const onChange = (e: MediaQueryListEvent) => setCoarse(e.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);
  return coarse;
}

const formatCurrency = (value: number) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);

/* -------------------------------------------------------------------------- */
/* Campo de dinheiro MASCARADO — todos os valores em R$ da tela passam por aqui */
/* -------------------------------------------------------------------------- */

/**
 * Máscara de moeda: os dígitos entram pela DIREITA, em centavos, e o campo se
 * formata sozinho — `1 → 0,01 · 15 → 0,15 · 150 → 1,50 · 1500 → 15,00 ·
 * 150000 → 1.500,00`. O usuário nunca digita separador, e é isso que mata a
 * ambiguidade do ponto na origem (CLAUDE.md #157): em pt-BR "1.500" é milhar,
 * mas no teclado do celular "30.50" sai como decimal — os dois palpites erram em
 * silêncio, um por ÷1000 e outro por ×100.
 *
 * `maskBRLInput` (escrita), `numberToMaskedBRL` (hidratação) e `parseBRLInput`
 * (leitura) andam SEMPRE juntos: formatar de um jeito e ler de outro corrompe o
 * valor na primeira edição.
 *
 * O commit é a cada tecla (não no blur) para o total do documento acompanhar a
 * digitação — e porque `blur` num campo mascarado não tem mais nada a resolver.
 */
/**
 * Trava o cursor no ÚLTIMO dígito do campo mascarado.
 *
 * Sem isso a máscara vira armadilha: o usuário toca no meio de "1.500,00", digita
 * um 7 e o resultado não é o que ele mirou — a máscara relê TODOS os dígitos e
 * reposiciona a vírgula, então uma tecla no meio move a casa decimal inteira. Como
 * os dígitos só entram pela direita, o cursor também só faz sentido à direita.
 *
 * `onSelect` cobre também o que acontece DEPOIS de digitar (o navegador pode
 * devolver o cursor para onde estava); a guarda evita o laço com o próprio
 * `setSelectionRange`.
 */
const caretToEnd = (e: React.SyntheticEvent<HTMLInputElement>) => {
  const el = e.currentTarget;
  const end = el.value.length;
  if (el.selectionStart !== end || el.selectionEnd !== end) {
    el.setSelectionRange(end, end);
  }
};

function MoneyInput({
  value,
  onCommit,
  className,
  id,
  placeholder = '0,00',
  compact,
}: {
  value: number | null | undefined;
  onCommit: (value: number) => void;
  className?: string;
  id?: string;
  placeholder?: string;
  /** Prefixo R$ menor, para o campo estreito da linha do item. */
  compact?: boolean;
}) {
  const [text, setText] = useState<string | null>(null);
  const shown = text ?? numberToMaskedBRL(value);

  return (
    <div className={cn('relative', className)}>
      <span
        className={cn(
          'pointer-events-none absolute top-1/2 -translate-y-1/2 text-muted-foreground',
          compact ? 'left-1.5 text-[10px]' : 'left-2.5 text-xs',
        )}
      >
        R$
      </span>
      <Input
        id={id}
        inputMode="numeric"
        placeholder={placeholder}
        value={shown}
        onChange={(e) => {
          const masked = maskBRLInput(e.target.value);
          setText(masked);
          onCommit(parseBRLInput(masked));
        }}
        onFocus={caretToEnd}
        onClick={caretToEnd}
        onSelect={caretToEnd}
        onBlur={() => setText(null)}
        className={cn(FIELD_INPUT, 'w-full text-right', compact ? 'pl-6 pr-2 text-sm' : 'pl-8')}
      />
    </div>
  );
}

/** Rótulo de seção — a "costura" que substitui o CardHeader dos cards reais. */
function SectionTitle({
  icon: Icon,
  children,
  aside,
}: {
  icon: React.ElementType;
  children: React.ReactNode;
  aside?: React.ReactNode;
}) {
  return (
    <div className="mb-2 flex items-center justify-between gap-2 px-1">
      <span className="flex items-center gap-2 text-sm font-semibold text-foreground">
        <Icon className="h-4 w-4 text-primary" />
        {children}
      </span>
      {aside}
    </div>
  );
}

/**
 * PADRÃO DE CAMPO EM LINHA COMPARTILHADA (o ajuste que faltava na 1ª versão).
 *
 * Dois campos lado a lado só ficam alinhados se o RÓTULO tiver altura fixa: um
 * rótulo com ícone (Data) é mais alto que um só de texto (Validade), e a
 * diferença empurra o input de baixo — foi exatamente o desalinhamento visto no
 * print. `FieldLabel` trava a altura em h-4 e `FIELD_INPUT` trava a altura do
 * controle em h-10; toda grade de campos usa os dois + `items-start`.
 */
function FieldLabel({ icon: Icon, children }: { icon?: React.ElementType; children: React.ReactNode }) {
  return (
    <Label className="flex h-4 items-center gap-1.5 text-xs font-medium">
      {Icon && <Icon className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />}
      {children}
    </Label>
  );
}

/** Altura/forma únicas de todo controle da tela (input, select, botão de campo). */
const FIELD_INPUT = 'h-10 rounded-xl bg-background';

/**
 * Campo de DESCONTO: seletor da unidade primeiro, valor depois — a unidade é o
 * que decide como o número é lido, então escolhê-la antes de digitar é a ordem
 * natural (e é ela que troca o tipo do campo ao lado).
 *
 * Os DOIS modos usam a mesma máscara (dígitos pela direita, 2 casas, cursor preso
 * no fim) — só muda o símbolo e o teto: percentual para em 100. Uma máscara num
 * campo e digitação livre no outro seria a pior combinação possível, porque a
 * mesma tecla produziria números diferentes conforme a unidade escolhida, e o
 * usuário não tem como saber qual regra está valendo naquele instante.
 */
function DiscountField({
  value,
  type,
  onValueChange,
  onTypeChange,
  className,
  id,
}: {
  value: number | null | undefined;
  type: 'value' | 'percent';
  onValueChange: (value: number) => void;
  onTypeChange: (type: 'value' | 'percent') => void;
  className?: string;
  id?: string;
}) {
  const [percentText, setPercentText] = useState<string | null>(null);

  return (
    <div className={cn('flex items-start gap-1', className)}>
      <Select value={type} onValueChange={(v) => onTypeChange(v as 'value' | 'percent')}>
        <SelectTrigger className={cn(FIELD_INPUT, 'w-16 shrink-0 px-2')}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="value">R$</SelectItem>
          <SelectItem value="percent">%</SelectItem>
        </SelectContent>
      </Select>
      {type === 'value' ? (
        <MoneyInput compact id={id} value={value} onCommit={onValueChange} className="min-w-0 flex-1" />
      ) : (
        <div className="relative min-w-0 flex-1">
          <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-[10px] text-muted-foreground">
            %
          </span>
          <Input
            id={id}
            inputMode="numeric"
            placeholder="0,00"
            value={percentText ?? numberToMaskedBRL(value)}
            onChange={(e) => {
              // Mesma máscara do R$: os dígitos entram pela direita em centésimos
              // (1 → 0,01 · 150 → 1,50 · 1000 → 10,00 · 10000 → 100,00).
              let text = maskBRLInput(e.target.value);
              // O TETO vale para o TEXTO, não só para o valor comitado: clampar
              // só o que é gravado deixa a tela mostrando "250,00%" enquanto a
              // conta usa 100% — tela e dado divergindo em silêncio.
              if (parseBRLInput(text) > 100) text = '100,00';
              setPercentText(text);
              onValueChange(parseBRLInput(text));
            }}
            onFocus={caretToEnd}
            onClick={caretToEnd}
            onSelect={caretToEnd}
            onBlur={() => setPercentText(null)}
            className={cn(FIELD_INPUT, 'w-full pr-6 text-right text-sm')}
          />
        </div>
      )}
    </div>
  );
}

/** Selo PRO — mesma pílula âmbar dos módulos (CLAUDE.md #24). */
function ProTag() {
  return (
    <span className="rounded-full bg-amber-500 px-1.5 py-0.5 text-[9px] font-bold leading-none text-white shadow">
      PRO
    </span>
  );
}

/* -------------------------------------------------------------------------- */
/* Cartão-bilhete de um item                                                    */
/* -------------------------------------------------------------------------- */

function ItemTicket({
  item,
  index,
  count,
  open,
  dragEnabled,
  onToggle,
  onPatch,
  onRemove,
  onMove,
}: {
  item: MockItem;
  index: number;
  count: number;
  open: boolean;
  /** Só no desktop (ponteiro fino). No toque, reordena pelas setas. */
  dragEnabled: boolean;
  onToggle: () => void;
  onPatch: (patch: Partial<MockItem>) => void;
  onRemove: () => void;
  onMove: (direction: -1 | 1) => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: item.id,
  });
  const [nameFocused, setNameFocused] = useState(false);
  const [lc116Query, setLc116Query] = useState('');

  const lineTotal = quoteItemLineTotal(item);

  // Sugestões do catálogo enquanto digita o nome — o mesmo atalho do formulário
  // real, que evita recadastrar um item que já existe.
  const suggestions = useMemo(() => {
    const q = item.name.trim().toLowerCase();
    if (!q || item.fromCatalog) return [];
    return MOCK_CATALOG.filter((p) => p.name.toLowerCase().includes(q)).slice(0, 4);
  }, [item.name, item.fromCatalog]);

  const lc116Results = useMemo(
    () => (lc116Query.trim() ? searchServicos(lc116Query).slice(0, 6) : []),
    [lc116Query],
  );

  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={cn(
        'relative overflow-hidden rounded-2xl border border-border/60 bg-card shadow-sm',
        isDragging && 'z-10 opacity-80 shadow-lg',
      )}
    >
      {/* Faixa de tipo à esquerda */}
      <span
        className={cn(
          'absolute inset-y-0 left-0 w-1.5',
          item.itemType === 'product' ? 'bg-amber-500' : 'bg-[#2f6fae]',
        )}
      />

      {/* Alça de arrasto — apenas com mouse */}
      {dragEnabled && (
        <div
          {...attributes}
          {...listeners}
          className="absolute bottom-0 left-1.5 top-0 flex w-7 cursor-grab items-center justify-center bg-muted/50 transition-colors hover:bg-muted active:cursor-grabbing"
          aria-label={`Arrastar item ${index + 1}`}
        >
          <GripVertical className="h-4 w-4 text-muted-foreground" />
        </div>
      )}

      <div className={cn('space-y-2 p-3', dragEnabled ? 'pl-[38px]' : 'pl-4')}>
        <div className="relative">
          <div className="flex items-center gap-2">
            <span
              className={cn(
                'flex h-6 w-6 shrink-0 items-center justify-center rounded-full',
                item.itemType === 'product'
                  ? 'bg-amber-500/15 text-amber-600'
                  : 'bg-[#2f6fae]/15 text-[#2f6fae]',
              )}
            >
              {item.itemType === 'product' ? (
                <ShoppingBag className="h-3.5 w-3.5" />
              ) : (
                <Wrench className="h-3.5 w-3.5" />
              )}
            </span>
            <Input
              placeholder={item.itemType === 'product' ? 'Nome do produto *' : 'Nome do serviço *'}
              value={item.name}
              onChange={(e) => onPatch({ name: e.target.value })}
              onFocus={() => setNameFocused(true)}
              // Atraso para o toque na sugestão registrar antes de o menu sumir.
              onBlur={() => setTimeout(() => setNameFocused(false), 150)}
              autoComplete="off"
              className="h-9 min-w-0 flex-1 rounded-xl border-transparent bg-muted/60 text-sm font-medium focus-visible:border-primary"
            />
          </div>

          {nameFocused && suggestions.length > 0 && (
            <div className="absolute left-8 right-0 top-full z-30 mt-1 max-h-56 overflow-y-auto rounded-xl border bg-popover shadow-md">
              {suggestions.map((product) => (
                <button
                  key={product.name}
                  type="button"
                  // onMouseDown roda ANTES do blur do input, senão o menu some
                  // e a escolha nunca chega.
                  onMouseDown={(e) => {
                    e.preventDefault();
                    onPatch({
                      name: product.name,
                      unit_price: product.price,
                      unit: product.unit,
                      itemType: product.type,
                      fromCatalog: true,
                    });
                    setNameFocused(false);
                  }}
                  className="flex w-full flex-col items-start gap-0.5 px-3 py-2 text-left hover:bg-accent"
                >
                  <span className="flex items-center gap-2 text-sm font-medium">
                    <Package className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                    {product.name}
                  </span>
                  <span className="pl-5 text-xs text-muted-foreground">
                    {formatCurrency(product.price)}
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>

        {/*
          LINHA 2 — quantidade, preço unitário e total NA MESMA LINHA. As larguras
          são medidas, não estimadas: sobram ~298px úteis dentro do card em 360px
          de tela, então o passo-a-passo fica em 82px fixos, o Total em 76px, e o
          preço absorve o resto com `flex-1`.
        */}
        <div className="flex items-center gap-1.5">
          {/* h-10 no CONTAINER (não nos filhos): com a altura nos botões, a borda
              do agrupador somava 2px e o passo-a-passo ficava 42px contra os 40px
              do campo de preço ao lado. */}
          <div className="flex h-10 shrink-0 items-center rounded-xl border border-border bg-background">
            <Button
              variant="ghost"
              size="icon"
              className="h-full w-6 rounded-l-xl"
              onClick={() => onPatch({ quantity: Math.max(0.01, +(item.quantity - 1).toFixed(3)) })}
              aria-label="Diminuir quantidade"
            >
              <Minus className="h-3.5 w-3.5" />
            </Button>
            <Input
              inputMode="decimal"
              value={String(item.quantity).replace('.', ',')}
              onChange={(e) => {
                const parsed = Number(e.target.value.replace(',', '.'));
                onPatch({ quantity: Number.isFinite(parsed) && parsed > 0 ? parsed : item.quantity });
              }}
              className="h-full w-8 border-0 bg-transparent px-0 text-center text-sm"
              aria-label="Quantidade"
            />
            <Button
              variant="ghost"
              size="icon"
              className="h-full w-6 rounded-r-xl"
              onClick={() => onPatch({ quantity: +(item.quantity + 1).toFixed(3) })}
              aria-label="Aumentar quantidade"
            >
              <Plus className="h-3.5 w-3.5" />
            </Button>
          </div>
          <MoneyInput
            compact
            value={item.unit_price}
            onCommit={(v) => onPatch({ unit_price: v })}
            className="min-w-0 flex-1"
          />
          {/* Picote do bilhete. O "R$" subiu para o RÓTULO: medido, `R$ 1.500,00`
              a 13px já estourava os 66px úteis da coluna, e valor cortado num
              total é o pior lugar possível para faltar espaço. */}
          <div className="w-[76px] shrink-0 border-l-2 border-dashed border-border pl-2 text-right">
            <span className="block text-[9px] uppercase leading-none tracking-wide text-muted-foreground">
              Total R$
            </span>
            <span className="block text-xs font-bold leading-tight text-primary">
              {numberToMaskedBRL(lineTotal) || '0,00'}
            </span>
          </div>
        </div>

        {/* LINHA 3 — mais opções, reordenar (toque) e remover */}
        <div className="flex items-center justify-between">
          <button
            type="button"
            onClick={onToggle}
            className="flex items-center gap-1 rounded-full px-2 py-1 text-[11px] font-medium text-primary hover:bg-primary/10"
          >
            {open ? 'Menos opções' : 'Mais opções'}
            <ChevronDown className={cn('h-3.5 w-3.5 transition-transform', open && 'rotate-180')} />
          </button>
          <div className="flex items-center gap-1">
            {item.fromCatalog && <span className="text-[10px] text-muted-foreground">do catálogo</span>}
            {!dragEnabled && count > 1 && (
              <>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7"
                  disabled={index === 0}
                  onClick={() => onMove(-1)}
                  aria-label={`Mover item ${index + 1} para cima`}
                >
                  <ArrowUp className="h-3.5 w-3.5" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7"
                  disabled={index === count - 1}
                  onClick={() => onMove(1)}
                  aria-label={`Mover item ${index + 1} para baixo`}
                >
                  <ArrowDown className="h-3.5 w-3.5" />
                </Button>
              </>
            )}
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7 text-destructive hover:bg-destructive/10"
              onClick={onRemove}
              aria-label={`Remover item ${index + 1}`}
            >
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          </div>
        </div>
      </div>

      {/* Detalhes do item */}
      {open && (
        <div
          className={cn(
            'space-y-3 border-t border-dashed border-border bg-muted/30 p-3',
            dragEnabled ? 'pl-[38px]' : 'pl-4',
          )}
        >
          <Textarea
            placeholder="Descrição / detalhamento (opcional)"
            value={item.description}
            onChange={(e) => onPatch({ description: e.target.value })}
            rows={2}
            className="rounded-xl bg-background"
          />

          {/* Fotos do item (3 slots) */}
          <div className="flex gap-2">
            {[0, 1, 2].map((slot) => (
              <button
                key={slot}
                type="button"
                onClick={() =>
                  onPatch({
                    photos: item.photos[slot]
                      ? item.photos.filter((_, i) => i !== slot)
                      : [...item.photos, `foto-${slot}`],
                  })
                }
                className={cn(
                  'flex h-16 flex-1 flex-col items-center justify-center gap-1 rounded-xl border-2 border-dashed text-[10px]',
                  item.photos[slot]
                    ? 'border-primary/50 bg-primary/10 text-primary'
                    : 'border-border text-muted-foreground hover:border-primary/40',
                )}
              >
                {item.photos[slot] ? <ImageIcon className="h-4 w-4" /> : <Camera className="h-4 w-4" />}
                Foto {slot + 1}
              </button>
            ))}
          </div>

          {/* Desconto + unidade */}
          <div className="grid grid-cols-2 items-start gap-3">
            <div className="space-y-1.5">
              <FieldLabel>Desconto</FieldLabel>
              <DiscountField
                value={item.discount}
                type={item.discount_type}
                onValueChange={(v) => onPatch({ discount: v })}
                onTypeChange={(t) => onPatch({ discount_type: t })}
              />
            </div>
            <div className="space-y-1.5">
              <FieldLabel>Unidade</FieldLabel>
              <Select value={item.unit} onValueChange={(v) => onPatch({ unit: v })}>
                <SelectTrigger className={FIELD_INPUT}>
                  <SelectValue placeholder="Selecione a unidade" />
                </SelectTrigger>
                <SelectContent className="max-h-72">
                  {unitGroupsIncluding(item.unit, item.itemType).map((group) => (
                    <SelectGroup key={group.label}>
                      <SelectLabel className="text-xs text-muted-foreground">{group.label}</SelectLabel>
                      {group.options.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectGroup>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Categoria + SKU */}
          <div className="grid grid-cols-2 items-start gap-3">
            <div className="space-y-1.5">
              <FieldLabel>Categoria</FieldLabel>
              <Input
                placeholder="Ex: Elétrica"
                value={item.category}
                onChange={(e) => onPatch({ category: e.target.value })}
                className={FIELD_INPUT}
              />
            </div>
            <div className="space-y-1.5">
              <FieldLabel>Código interno (SKU)</FieldLabel>
              <div className="flex items-start gap-1">
                <Input
                  placeholder="SRV-001"
                  value={item.sku}
                  onChange={(e) => onPatch({ sku: e.target.value })}
                  className={cn(FIELD_INPUT, 'min-w-0')}
                />
                <Button
                  variant="outline"
                  size="icon"
                  className="h-10 w-10 shrink-0 rounded-xl"
                  onClick={() =>
                    onPatch({
                      sku: `${item.itemType === 'product' ? 'PRD' : 'SRV'}-${Math.random()
                        .toString(36)
                        .slice(2, 8)
                        .toUpperCase()}`,
                    })
                  }
                  aria-label="Gerar código interno"
                >
                  <Sparkles className="h-4 w-4 text-primary" />
                </Button>
              </div>
            </div>
          </div>

          {item.itemType === 'product' ? (
            /* Estoque e código de barras — só produto */
            <div className="space-y-3 rounded-xl border border-amber-500/30 bg-amber-500/5 p-3">
              <div className="flex items-center justify-between gap-2">
                <div>
                  <Label className="text-xs font-medium">Controlar estoque</Label>
                  <p className="text-[10px] text-muted-foreground">Monitorar entradas e saídas</p>
                </div>
                <Switch
                  checked={item.trackStock}
                  onCheckedChange={(v) => onPatch({ trackStock: v })}
                />
              </div>
              {item.trackStock && (
                <div className="space-y-1.5">
                  <FieldLabel>Estoque mínimo</FieldLabel>
                  <Input
                    inputMode="numeric"
                    placeholder="Ex: 5"
                    value={item.minStock}
                    onChange={(e) => onPatch({ minStock: e.target.value })}
                    className={FIELD_INPUT}
                  />
                </div>
              )}
              <div className="space-y-1.5">
                <FieldLabel>Código de barras</FieldLabel>
                <div className="flex items-start gap-1">
                  <Input
                    placeholder="Código de barras"
                    value={item.barcode}
                    onChange={(e) => onPatch({ barcode: e.target.value })}
                    className={cn(FIELD_INPUT, 'min-w-0')}
                  />
                  <Button
                    variant="outline"
                    size="icon"
                    className="h-10 w-10 shrink-0 rounded-xl"
                    onClick={() => toast.info('Protótipo: abriria o leitor.')}
                    aria-label="Ler código de barras"
                  >
                    <Barcode className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </div>
          ) : (
            /* Classificação fiscal — só serviço (alimenta a NFS-e) */
            <div className="space-y-3 rounded-xl border border-[#2f6fae]/30 bg-[#2f6fae]/5 p-3">
              <div className="space-y-1.5">
                <FieldLabel>Serviço (LC 116)</FieldLabel>
                <div className="relative">
                  <Input
                    placeholder="Buscar por descrição ou código"
                    value={lc116Query}
                    onChange={(e) => setLc116Query(e.target.value)}
                    className={FIELD_INPUT}
                  />
                  {lc116Results.length > 0 && (
                    <div className="absolute left-0 right-0 top-full z-40 mt-1 max-h-44 overflow-y-auto rounded-xl border bg-popover shadow-md">
                      {lc116Results.map((servico) => (
                        <button
                          key={servico.codigo}
                          type="button"
                          onMouseDown={(e) => {
                            e.preventDefault();
                            // A correlação NBS/cClassTrib vem junto do código, como
                            // no formulário real — escolher o serviço já classifica
                            // o item para a nota.
                            getCorrelacaoByLC116(servico.codigo);
                            onPatch({ lc116: servico.codigo });
                            setLc116Query('');
                          }}
                          className="block w-full px-3 py-1.5 text-left text-xs hover:bg-accent"
                        >
                          <span className="font-mono text-[10px] text-muted-foreground">
                            {servico.codigo}
                          </span>{' '}
                          — {servico.descricao}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                {item.lc116 && (
                  <div className="flex items-center justify-between rounded-md bg-muted/60 p-1 text-xs text-muted-foreground">
                    <span>
                      Item LC116: <span className="font-mono font-semibold">{item.lc116}</span>
                    </span>
                    <button
                      type="button"
                      className="text-[10px] text-destructive hover:underline"
                      onClick={() => onPatch({ lc116: '' })}
                    >
                      limpar
                    </button>
                  </div>
                )}
              </div>
              <div className="space-y-1.5">
                <FieldLabel>Alíquota ISS (%)</FieldLabel>
                <Input
                  inputMode="decimal"
                  placeholder="Ex: 5"
                  value={item.issAliquota}
                  onChange={(e) => onPatch({ issAliquota: e.target.value.replace(/[^\d.,]/g, '') })}
                  className={FIELD_INPUT}
                />
              </div>
            </div>
          )}

          {/* Salvar no catálogo + custo */}
          {!item.fromCatalog && (
            <div className="space-y-2 rounded-xl border border-primary/25 bg-primary/5 p-3">
              <div className="flex items-center gap-2">
                <Checkbox
                  id={`p4-catalog-${item.id}`}
                  checked={item.saveToCatalog}
                  onCheckedChange={(v) => onPatch({ saveToCatalog: v === true })}
                  onClick={(e) => e.stopPropagation()}
                />
                <div
                  className="cursor-pointer text-sm leading-tight"
                  onClick={() => onPatch({ saveToCatalog: !item.saveToCatalog })}
                >
                  Salvar este item no meu catálogo
                </div>
              </div>
              {item.saveToCatalog && (
                <div className="space-y-1.5">
                  <FieldLabel>Custo (opcional)</FieldLabel>
                  <MoneyInput
                    value={item.cost}
                    onCommit={(v) => onPatch({ cost: v || null })}
                    className="w-32"
                  />
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Página                                                                      */
/* -------------------------------------------------------------------------- */

export default function TestNewQuotePage4() {
  const navigate = useNavigate();
  const coarsePointer = useCoarsePointer();
  const { isOnline } = useNetworkStatus();

  // --- cabeçalho / metadados
  const [sequenceId] = useState(42);
  const [showSequenceId, setShowSequenceId] = useState(true);
  const [title, setTitle] = useState('');
  const [quoteDate, setQuoteDate] = useState(() => new Date().toISOString().split('T')[0]);
  const [status, setStatus] = useState<QuoteStatus>('pending');
  const [documentType, setDocumentType] = useState('orcamento');

  // --- cliente (os MESMOS campos de /clients/new — ver ClientFormPage)
  const [client, setClient] = useState<MockClient | null>(null);
  const [clientName, setClientName] = useState('');
  const [clientPhone, setClientPhone] = useState('');
  const [clientDocument, setClientDocument] = useState('');
  const [clientEmail, setClientEmail] = useState('');
  const [cep, setCep] = useState('');
  const [cepLoading, setCepLoading] = useState(false);
  const [logradouro, setLogradouro] = useState('');
  const [numero, setNumero] = useState('');
  const [complemento, setComplemento] = useState('');
  const [bairro, setBairro] = useState('');
  const [municipioNome, setMunicipioNome] = useState('');
  const [uf, setUf] = useState('');
  const [inscricaoEstadual, setInscricaoEstadual] = useState('');
  const [saveNewClient, setSaveNewClient] = useState(false);
  const [clientSelectorOpen, setClientSelectorOpen] = useState(false);
  const [clientInputFocused, setClientInputFocused] = useState(false);

  const clientSuggestions = useMemo(() => {
    const q = clientName.trim().toLowerCase();
    if (!q || client) return [];
    return MOCK_CLIENTS.filter((c) => c.name.toLowerCase().includes(q)).slice(0, 4);
  }, [clientName, client]);

  const docKind = detectDocumentKind(clientDocument);
  const docValid = isValidDocument(clientDocument);

  /** Busca de endereço pelo CEP — mesmo helper (`lookupCep`) do /clients/new. */
  const handleCepLookup = async (rawCep: string) => {
    const digits = onlyDigits(rawCep);
    if (digits.length !== 8) return;
    setCepLoading(true);
    try {
      const addr = await lookupCep(digits);
      if (addr) {
        if (addr.logradouro) setLogradouro(addr.logradouro);
        if (addr.bairro) setBairro(addr.bairro);
        if (addr.municipioNome) setMunicipioNome(addr.municipioNome);
        if (addr.uf) setUf(addr.uf);
      } else {
        toast.warning('CEP não encontrado.');
      }
    } finally {
      setCepLoading(false);
    }
  };

  // --- itens
  const [items, setItems] = useState<MockItem[]>([]);
  const [expandedItem, setExpandedItem] = useState<string | null>(null);
  const [catalogOpen, setCatalogOpen] = useState(false);

  // --- total
  const [shipping, setShipping] = useState(0);
  const [discount, setDiscount] = useState(0);
  const [discountType, setDiscountType] = useState<'value' | 'percent'>('value');
  const [extrasOpen, setExtrasOpen] = useState(false);

  // --- campos extras
  const [validityDays, setValidityDays] = useState('7');
  const [warrantyAmount, setWarrantyAmount] = useState<number | null>(null);
  const [warrantyUnit, setWarrantyUnit] = useState<WarrantyUnit>('days');
  /** Garantia em TEXTO LIVRE — o campo histórico, que continua existindo. */
  const [warranty, setWarranty] = useState('');
  /**
   * Modo do campo de garantia. Precisa ser EXPLÍCITO: a 1ª versão marcava o modo
   * texto livre escrevendo um espaço em `warranty`, e o `trim()` da condição de
   * exibição descartava justo esse espaço — o botão não fazia nada.
   */
  const [warrantyFreeMode, setWarrantyFreeMode] = useState(false);
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>(['pix']);
  const [paymentTerms, setPaymentTerms] = useState('');
  const [reference, setReference] = useState('');
  const [notes, setNotes] = useState('');
  const [terms, setTerms] = useState('');
  const [pdfTemplate, setPdfTemplate] = useState('classico');
  const [generalPhotos, setGeneralPhotos] = useState<GeneralPhoto[]>([]);
  const [editingPhoto, setEditingPhoto] = useState<GeneralPhoto | null>(null);

  // --- estado de gravação (habilita Copiar link / Visualizar)
  const [savedId, setSavedId] = useState<string | null>(null);

  const totals = useMemo(
    () => computeQuoteTotals({ items, shipping, discount, discountType }),
    [items, shipping, discount, discountType],
  );

  const patchItem = (id: string, patch: Partial<MockItem>) =>
    setItems((prev) => prev.map((it) => (it.id === id ? { ...it, ...patch } : it)));

  /* Reordenação: arrasto com mouse, setas no toque (ver useCoarsePointer). */
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setItems((prev) => {
      const from = prev.findIndex((it) => it.id === active.id);
      const to = prev.findIndex((it) => it.id === over.id);
      return from < 0 || to < 0 ? prev : arrayMove(prev, from, to);
    });
  };

  const moveItem = (index: number, direction: -1 | 1) =>
    setItems((prev) => {
      const to = index + direction;
      return to < 0 || to >= prev.length ? prev : arrayMove(prev, index, to);
    });

  const addItem = (itemType: 'service' | 'product') => {
    const item = emptyItem(itemType);
    setItems((prev) => [...prev, item]);
    setExpandedItem(item.id);
  };

  const addFromCatalog = (entry: (typeof MOCK_CATALOG)[number]) => {
    setItems((prev) => [
      ...prev,
      {
        ...emptyItem(entry.type),
        name: entry.name,
        unit_price: entry.price,
        unit: entry.unit,
        fromCatalog: true,
      },
    ]);
    setCatalogOpen(false);
  };

  const togglePaymentMethod = (method: PaymentMethod) =>
    setPaymentMethods((prev) =>
      prev.includes(method) ? prev.filter((m) => m !== method) : [...prev, method],
    );

  const handleSave = () => {
    if (!clientName.trim()) return toast.warning('Informe o nome do cliente');
    if (items.length === 0) return toast.warning('Adicione pelo menos um item ao orçamento');
    if (items.some((it) => !it.name.trim())) return toast.warning('Preencha o nome de todos os itens');
    setSavedId(createUuid());
    toast.success('Protótipo: orçamento "salvo" (nada foi gravado no banco).');
  };

  const docLabel = DOCUMENT_TYPE_LABELS[documentType] ?? 'Orçamento';

  return (
    <MainLayout showFab={false}>
      <div className="pb-4">
        {/* ================================================================== */}
        {/* HERO — sangrado, curva embaixo, total ao vivo                       */}
        {/* ================================================================== */}
        <div className="relative -mx-4 -mt-6 overflow-hidden rounded-b-[2rem] bg-gradient-to-br from-primary via-primary to-emerald-600 px-4 pb-14 pt-6 text-white shadow-[0_10px_30px_-12px_hsla(var(--primary)/0.6)]">
          {/* Blobs decorativos */}
          <div className="pointer-events-none absolute -right-10 -top-16 h-44 w-44 rounded-full bg-white/10" />
          <div className="pointer-events-none absolute -left-16 top-16 h-40 w-40 rounded-full bg-white/5" />

          <div className="relative flex items-center gap-2">
            <Button
              variant="ghost"
              size="icon"
              className="h-9 w-9 rounded-full bg-white/15 text-white hover:bg-white/25 hover:text-white"
              onClick={() => navigate(-1)}
              aria-label="Voltar"
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div className="min-w-0 flex-1">
              <p className="text-[11px] uppercase tracking-wide text-white/70">Novo documento</p>
              <h1 className="truncate text-lg font-bold leading-tight">{docLabel}</h1>
            </div>
            <div className="flex items-center gap-1.5 rounded-full bg-white/15 px-2.5 py-1">
              <Hash className="h-3.5 w-3.5 text-white/80" />
              <span className="font-mono text-sm font-bold leading-none">
                {String(sequenceId).padStart(4, '0')}
              </span>
            </div>
          </div>

          {/* Total ao vivo + nº no PDF */}
          <div className="relative mt-5 flex items-end justify-between gap-3">
            <div className="min-w-0">
              <p className="text-xs text-white/75">Total do {docLabel.toLowerCase()}</p>
              <p className="truncate text-3xl font-bold leading-tight">{formatCurrency(totals.total)}</p>
              <p className="mt-0.5 text-xs text-white/70">
                {items.length === 0
                  ? 'Nenhum item ainda'
                  : `${items.length} ${items.length === 1 ? 'item' : 'itens'}`}
              </p>
            </div>
            {/*
              O trilho do switch precisa ser ESCURO: o thumb do componente é
              `bg-background` (branco), então um track branco/claro sobre o hero
              verde deixava o controle praticamente invisível — não dava para
              saber se estava ligado.
            */}
            <label className="flex shrink-0 cursor-pointer items-center gap-2 rounded-full bg-emerald-950/25 px-3 py-1.5">
              <span className="text-[11px] leading-none text-white">Nº no PDF</span>
              <Switch
                checked={showSequenceId}
                onCheckedChange={setShowSequenceId}
                className="scale-90 data-[state=checked]:bg-emerald-900 data-[state=unchecked]:bg-emerald-950/60"
              />
            </label>
          </div>

          {/*
            Status: os 4 são um CONJUNTO e cabem na largura da tela — grade de 4
            colunas, não trilho rolável. Rolagem horizontal escondia "Cancelado"
            atrás da borda e parecia vazamento de layout.
          */}
          <div className="mt-4 grid grid-cols-4 gap-1.5">
            {STATUS_OPTIONS.map((option) => {
              const active = option.value === status;
              return (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => setStatus(option.value)}
                  className={cn(
                    'flex min-w-0 items-center justify-center gap-1 rounded-full px-1 py-1.5 text-[11px] font-medium transition-all',
                    active
                      ? 'bg-white text-emerald-800 shadow-[inset_0_-3px_0_rgba(0,0,0,0.12)]'
                      : 'bg-white/15 text-white/85 hover:bg-white/25',
                  )}
                >
                  <span className={cn('h-1.5 w-1.5 shrink-0 rounded-full', option.dot)} />
                  <span className="truncate">{option.label}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* ================================================================== */}
        {/* CARTÃO FLUTUANTE — Cliente                                          */}
        {/* ================================================================== */}
        <div className="relative z-10 -mt-9 rounded-2xl border border-border/60 bg-card p-4 shadow-lg">
          {client ? (
            /*
              Tudo numa linha só. O avatar saiu: cliente não tem foto neste app, então
              aquele círculo era um espaço reservado para nada — e ele custava 56px
              (44 do círculo + 12 da sarjeta) justamente do nome. MEDIDO: com ele, o
              nome tinha 106px e "Ana Paula Ribeiro" (112px) já truncava; sem ele
              sobram ~162px e o pior caso do fixture ("Condomínio Vila Nova", 143px)
              cabe inteiro na mesma linha dos três botões.
            */
            <div className="flex items-center gap-2">
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-semibold">{client.name}</p>
                <p className="truncate text-xs text-muted-foreground">{client.phone}</p>
              </div>
              {/* Só o símbolo: o ícone do WhatsApp e o de troca já dizem a ação. Botão
                  só de ícone não tem nome acessível — daí `aria-label` + `title`
                  (CLAUDE.md #142). */}
              <Button3D
                color="whatsapp"
                size="sm"
                className="h-8 w-8 shrink-0 px-0"
                onClick={() => toast.info('Protótipo: abriria o WhatsApp do cliente.')}
                aria-label="Abrir WhatsApp do cliente"
                title="Abrir WhatsApp"
              >
                <WhatsAppIcon className="h-4 w-4" />
              </Button3D>
              <Button3D
                color="gray"
                size="sm"
                className="h-8 w-8 shrink-0 px-0"
                onClick={() => setClientSelectorOpen(true)}
                aria-label="Trocar cliente"
                title="Trocar cliente"
              >
                <RefreshCw className="h-4 w-4" />
              </Button3D>
              <Button3D
                color="red"
                size="sm"
                className="h-8 w-8 shrink-0 px-0"
                onClick={() => {
                  setClient(null);
                  setClientName('');
                }}
                aria-label="Limpar cliente"
                title="Remover cliente do documento"
              >
                <X className="h-4 w-4" />
              </Button3D>
            </div>
          ) : (
            <>
              {/*
                O botão saiu de DENTRO do campo: ali ele comia ~96px e cortava o
                placeholder ("Buscar ou digitar o nom…"), além de não parecer um
                botão — era só um texto verde sobre o fundo do input.
              */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  placeholder="Nome do cliente *"
                  value={clientName}
                  onChange={(e) => setClientName(e.target.value)}
                  onFocus={() => setClientInputFocused(true)}
                  // Atraso para o toque na sugestão registrar antes de o menu sumir.
                  onBlur={() => setTimeout(() => setClientInputFocused(false), 150)}
                  autoComplete="off"
                  className="h-12 rounded-xl border-border/70 bg-background pl-10"
                />
                {/* Autocomplete dos clientes já cadastrados — o mesmo atalho do
                    formulário real, que evita cadastrar o mesmo cliente duas vezes. */}
                {clientInputFocused && clientSuggestions.length > 0 && (
                  <div className="absolute left-0 right-0 top-full z-30 mt-1 max-h-56 overflow-y-auto rounded-xl border bg-popover shadow-md">
                    {clientSuggestions.map((c) => (
                      <button
                        key={c.id}
                        type="button"
                        onMouseDown={(e) => {
                          e.preventDefault();
                          setClient(c);
                          setClientName(c.name);
                          setClientInputFocused(false);
                        }}
                        className="flex w-full flex-col items-start gap-0.5 px-3 py-2 text-left hover:bg-accent"
                      >
                        <span className="flex items-center gap-2 text-sm font-medium">
                          <User className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                          {c.name}
                        </span>
                        <span className="flex flex-wrap gap-x-3 pl-5 text-xs text-muted-foreground">
                          <span className="flex items-center gap-1">
                            <Phone className="h-3 w-3" />
                            {c.phone}
                          </span>
                          <span className="flex items-center gap-1">
                            <MapPin className="h-3 w-3" />
                            {c.address}
                          </span>
                        </span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <Button
                type="button"
                variant="outline"
                className="mt-2 h-10 w-full gap-2 rounded-xl border-primary/40 bg-primary/5 text-sm font-semibold text-primary hover:bg-primary/10 hover:text-primary"
                onClick={() => setClientSelectorOpen(true)}
              >
                <User className="h-4 w-4" />
                Escolher dos meus clientes
              </Button>

              {/*
                Cadastro rápido — os MESMOS campos de /clients/new (ClientFormPage),
                na mesma ordem, com os mesmos rótulos, placeholders e validações
                (detecção CPF/CNPJ, máscara de CEP e busca de endereço no blur).
                Ter dois formulários do mesmo cadastro divergindo é o começo de um
                campo existir num lugar e sumir no outro.
              */}
              {clientName.trim() && (
                <div className="mt-3 space-y-3 border-t border-dashed border-border pt-3 duration-300 animate-in fade-in slide-in-from-top-2">
                  <p className="flex items-center gap-2 text-xs text-muted-foreground">
                    <UserPlus className="h-3.5 w-3.5" />
                    Dados adicionais do cliente (opcional)
                  </p>

                  <div className="grid gap-3 sm:grid-cols-2">
                    <div className="space-y-1.5">
                      <FieldLabel icon={FileText}>CPF / CNPJ</FieldLabel>
                      <Input
                        inputMode="numeric"
                        placeholder="CPF ou CNPJ"
                        value={formatDocument(clientDocument)}
                        onChange={(e) => setClientDocument(onlyDigits(e.target.value))}
                        className={cn(FIELD_INPUT, docValid === false && 'border-destructive')}
                      />
                      {docKind && docValid !== false && (
                        <p className="text-[11px] text-muted-foreground">
                          {docKind === 'cpf' ? 'CPF' : 'CNPJ'} detectado.
                        </p>
                      )}
                      {docValid === false && (
                        <p className="text-[11px] text-destructive">Dígitos não conferem.</p>
                      )}
                    </div>
                    {/*
                      "Agendar" mora DENTRO do campo: ele só existe com o telefone
                      completo (11+ dígitos), então o texto nunca chega perto da
                      borda direita e sobra espaço ali. Embaixo, em largura cheia,
                      ele roubava atenção de um atalho opcional.
                    */}
                    <div className="space-y-1.5">
                      <FieldLabel icon={Phone}>WhatsApp / telefone</FieldLabel>
                      <div className="relative">
                        <Input
                          placeholder="(00) 00000-0000"
                          value={clientPhone}
                          onChange={(e) => setClientPhone(e.target.value)}
                          className={cn(FIELD_INPUT, onlyDigits(clientPhone).length >= 11 && 'pr-[96px]')}
                        />
                        {onlyDigits(clientPhone).length >= 11 && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="absolute right-1 top-1/2 h-8 -translate-y-1/2 gap-1 rounded-lg border border-primary/30 bg-primary/5 px-2 text-[11px] font-semibold text-primary hover:bg-primary/10 hover:text-primary"
                            onClick={() => toast.info('Protótipo: salvaria o contato na agenda do celular.')}
                          >
                            <Contact className="h-3.5 w-3.5" />
                            Agendar
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="space-y-1.5">
                    <FieldLabel>E-mail</FieldLabel>
                    <Input
                      type="email"
                      placeholder="cliente@email.com"
                      value={clientEmail}
                      onChange={(e) => setClientEmail(e.target.value)}
                      className={FIELD_INPUT}
                    />
                  </div>

                  {/* Endereço estruturado (para nota fiscal), igual ao /clients/new */}
                  <div className="space-y-3 rounded-xl border border-border p-3">
                    <p className="flex items-center gap-2 text-xs font-medium text-muted-foreground">
                      <MapPin className="h-3.5 w-3.5" />
                      Endereço (para nota fiscal)
                    </p>

                    <div className="grid grid-cols-3 items-start gap-3">
                      <div className="space-y-1.5">
                        <FieldLabel>CEP</FieldLabel>
                        <div className="relative">
                          <Input
                            inputMode="numeric"
                            placeholder="00000-000"
                            value={formatCep(cep)}
                            onChange={(e) => setCep(onlyDigits(e.target.value))}
                            onBlur={(e) => handleCepLookup(e.target.value)}
                            className={cn(FIELD_INPUT, 'px-2.5 text-sm')}
                          />
                          {cepLoading && (
                            <Loader2 className="absolute right-2 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-muted-foreground" />
                          )}
                        </div>
                      </div>
                      <div className="col-span-2 space-y-1.5">
                        <FieldLabel>Logradouro</FieldLabel>
                        <Input
                          placeholder="Rua, avenida..."
                          value={logradouro}
                          onChange={(e) => setLogradouro(e.target.value)}
                          className={FIELD_INPUT}
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-3 items-start gap-3">
                      <div className="space-y-1.5">
                        <FieldLabel>Número</FieldLabel>
                        <Input
                          placeholder="123"
                          value={numero}
                          onChange={(e) => setNumero(e.target.value)}
                          className={cn(FIELD_INPUT, 'px-2.5 text-sm')}
                        />
                      </div>
                      <div className="col-span-2 space-y-1.5">
                        <FieldLabel>Complemento</FieldLabel>
                        <Input
                          placeholder="Apto, sala, bloco..."
                          value={complemento}
                          onChange={(e) => setComplemento(e.target.value)}
                          className={FIELD_INPUT}
                        />
                      </div>
                    </div>

                    <div className="space-y-1.5">
                      <FieldLabel>Bairro</FieldLabel>
                      <Input
                        placeholder="Bairro / distrito"
                        value={bairro}
                        onChange={(e) => setBairro(e.target.value)}
                        className={FIELD_INPUT}
                      />
                    </div>

                    <div className="grid grid-cols-3 items-start gap-3">
                      <div className="col-span-2 space-y-1.5">
                        <FieldLabel>Município</FieldLabel>
                        <Input
                          placeholder="Cidade"
                          value={municipioNome}
                          onChange={(e) => setMunicipioNome(e.target.value)}
                          className={FIELD_INPUT}
                        />
                      </div>
                      <div className="space-y-1.5">
                        <FieldLabel>UF</FieldLabel>
                        <Input
                          maxLength={2}
                          placeholder="UF"
                          value={uf}
                          onChange={(e) => setUf(e.target.value.toUpperCase().replace(/[^A-Z]/g, ''))}
                          className={cn(FIELD_INPUT, 'px-2.5 text-sm')}
                        />
                      </div>
                    </div>

                    <div className="space-y-1.5">
                      <FieldLabel>Inscrição estadual</FieldLabel>
                      <Input
                        placeholder="ISENTO para consumidor final"
                        value={inscricaoEstadual}
                        onChange={(e) => setInscricaoEstadual(e.target.value)}
                        className={FIELD_INPUT}
                      />
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <Checkbox
                      id="p4-save-client"
                      checked={saveNewClient}
                      onCheckedChange={(v) => setSaveNewClient(v === true)}
                      onClick={(e) => e.stopPropagation()}
                    />
                    <div
                      className="cursor-pointer text-sm leading-tight"
                      onClick={() => setSaveNewClient((v) => !v)}
                    >
                      Salvar este cliente na minha lista
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* ================================================================== */}
        {/* LADRILHOS — como adicionar item                                     */}
        {/* ================================================================== */}
        <div className="mt-5 grid grid-cols-4 gap-2">
          {[
            { icon: Package, label: 'Catálogo', onClick: () => setCatalogOpen(true), tint: 'text-primary bg-primary/10' },
            { icon: Barcode, label: 'Código', onClick: () => toast.info('Protótipo: abriria o leitor de código de barras.'), tint: 'text-slate-600 bg-slate-500/10' },
            { icon: Wrench, label: 'Serviço', onClick: () => addItem('service'), tint: 'text-[#2f6fae] bg-[#2f6fae]/10' },
            { icon: ShoppingBag, label: 'Produto', onClick: () => addItem('product'), tint: 'text-amber-600 bg-amber-500/10' },
          ].map((tile) => (
            <button
              key={tile.label}
              type="button"
              onClick={tile.onClick}
              className="flex flex-col items-center gap-1.5 rounded-2xl border border-border/60 bg-card px-1 py-3 transition-all hover:-translate-y-0.5 hover:shadow-md active:scale-[0.98]"
            >
              <span className={cn('flex h-9 w-9 items-center justify-center rounded-full', tile.tint)}>
                <tile.icon className="h-4.5 w-4.5" />
              </span>
              <span className="text-[11px] font-medium text-foreground">{tile.label}</span>
            </button>
          ))}
        </div>

        {/* ================================================================== */}
        {/* RAMIFICAÇÃO — liga os 4 atalhos ao card de itens                    */}
        {/* ------------------------------------------------------------------ */}
        {/* Desenhada com bordas (não SVG) para acompanhar a largura da tela sem
            distorcer traço: um talo sob o centro de cada ladrilho, um barramento
            horizontal que vai do 1º ao 4º centro (`mx-[12.5%]` = exatamente o
            centro das colunas de uma grade de 4) e uma descida central até o topo
            do card. É o que torna explícito que os botões ALIMENTAM a lista. */}
        <div aria-hidden className="pointer-events-none select-none">
          {/* talos: mesma grade (grid-cols-4 gap-2) dos ladrilhos, senão os
              traços não caem no centro dos botões */}
          <div className="grid grid-cols-4 gap-2">
            {[0, 1, 2, 3].map((i) => (
              <span key={i} className="mx-auto h-3 w-px bg-primary/30" />
            ))}
          </div>
          {/* barramento: cada célula avança meia sarjeta (-mx-1 de um gap-2)
              para o traço atravessar os vãos sem emenda */}
          <div className="grid grid-cols-4 gap-2">
            <span className="-mr-1 ml-[50%] block border-t border-primary/30" />
            <span className="-mx-1 block border-t border-primary/30" />
            <span className="-mx-1 block border-t border-primary/30" />
            <span className="-ml-1 mr-[50%] block border-t border-primary/30" />
          </div>
          {/* a descida central ENCOSTA no card (sem ponta): o ponto solto acima
              da borda parecia um traço interrompido */}
          <div className="mx-auto h-4 w-px bg-primary/30" />
        </div>

        {/* ================================================================== */}
        {/* ITENS — cartões-bilhete                                             */}
        {/* ================================================================== */}
        <div className="rounded-2xl border border-border/60 bg-card p-3">
          <div className="mb-3 flex items-center justify-between gap-2">
            <span className="flex items-center gap-2 text-sm font-semibold">
              <Package className="h-4 w-4 text-primary" />
              Itens do {docLabel.toLowerCase()}
            </span>
            <span className="text-[11px] text-muted-foreground">
              Fotos: {items.reduce((acc, it) => acc + it.photos.length, 0)}/3 no grátis
            </span>
          </div>

          {items.length === 0 ? (
            <div className="flex flex-col items-center justify-center rounded-xl border-2 border-dashed border-primary/30 bg-primary/5 px-4 py-8 text-center">
              <Package className="mb-2 h-9 w-9 text-primary/40" />
              <p className="text-sm text-muted-foreground">
                Nenhum item ainda.
                <br />
                Use os atalhos acima para começar.
              </p>
            </div>
          ) : (
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={items.map((it) => it.id)}
                strategy={verticalListSortingStrategy}
              >
                <div className="space-y-3">
                  {items.map((item, index) => (
                    <ItemTicket
                      key={item.id}
                      item={item}
                      index={index}
                      count={items.length}
                      open={expandedItem === item.id}
                      dragEnabled={!coarsePointer}
                      onToggle={() => setExpandedItem(expandedItem === item.id ? null : item.id)}
                      onPatch={(patch) => patchItem(item.id, patch)}
                      onRemove={() => setItems((prev) => prev.filter((it) => it.id !== item.id))}
                      onMove={(direction) => moveItem(index, direction)}
                    />
                  ))}
                </div>
              </SortableContext>
            </DndContext>
          )}
        </div>

        {/* ================================================================== */}
        {/* TOTAL — cartão escuro                                               */}
        {/* ================================================================== */}
        <div className="mt-6 overflow-hidden rounded-2xl bg-gradient-to-br from-emerald-800 to-emerald-950 p-4 text-white shadow-lg">
          {extrasOpen ? (
            <div className="mb-3 flex flex-wrap items-end gap-3 rounded-xl bg-white/10 p-3">
              <div className="space-y-1">
                <Label htmlFor="p4-shipping" className="text-[11px] text-white/70">
                  Frete
                </Label>
                <MoneyInput
                  id="p4-shipping"
                  value={shipping}
                  onCommit={setShipping}
                  className="w-24 text-foreground"
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="p4-discount" className="text-[11px] text-white/70">
                  Desconto no total
                </Label>
                <DiscountField
                  id="p4-discount"
                  value={discount}
                  type={discountType}
                  onValueChange={setDiscount}
                  onTypeChange={setDiscountType}
                  className="w-[136px] text-foreground"
                />
              </div>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => setExtrasOpen(true)}
              className="mb-3 flex items-center gap-1 rounded-full bg-white/10 px-3 py-1.5 text-xs text-white/85 hover:bg-white/20"
            >
              <Plus className="h-3.5 w-3.5" /> Frete ou desconto no total
            </button>
          )}

          {/* Decomposição real (subtotal → descontos → frete) */}
          <div className="[&_span]:text-white/70">
            <QuoteTotalsBreakdown
              totals={totals}
              formatCurrency={formatCurrency}
              className="border-b border-white/15 pb-3"
            />
          </div>

          <div className="mt-3 flex items-end justify-between">
            <span className="text-sm text-white/75">Total</span>
            <span className="text-3xl font-bold">{formatCurrency(totals.total)}</span>
          </div>
        </div>

        {/* ================================================================== */}
        {/* DETALHES DO DOCUMENTO                                               */}
        {/* ================================================================== */}
        <div className="mt-6">
          <SectionTitle icon={Pencil}>Detalhes do documento</SectionTitle>
          <Accordion type="single" collapsible className="space-y-3">
            {/* --- identificação */}
            <AccordionItem
              value="identificacao"
              className="overflow-hidden rounded-2xl border border-border/60 bg-card px-4"
            >
              <AccordionTrigger className="py-3.5 text-sm font-medium hover:no-underline">
                <span className="flex items-center gap-2">
                  <FileText className="h-4 w-4 text-primary" />
                  Identificação
                </span>
              </AccordionTrigger>
              <AccordionContent className="space-y-3 pb-4">
                <div className="space-y-1.5">
                  <FieldLabel icon={Pencil}>Título (opcional)</FieldLabel>
                  <Input
                    placeholder="Ex: Reforma da cozinha"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    className={FIELD_INPUT}
                  />
                </div>
                {/* 3/5 para a data: com metade da linha, o widget nativo de data
                    espremia "2026" contra o ícone de calendário e o ano saía
                    cortado. Validade cabe folgado em 2/5. */}
                <div className="grid grid-cols-5 items-start gap-3">
                  <div className="col-span-3 space-y-1.5">
                    <FieldLabel icon={Calendar}>Data</FieldLabel>
                    <Input
                      type="date"
                      value={quoteDate}
                      onChange={(e) => setQuoteDate(e.target.value)}
                      className={cn(FIELD_INPUT, 'px-2.5 text-sm')}
                    />
                  </div>
                  <div className="col-span-2 space-y-1.5">
                    <FieldLabel>Validade (dias)</FieldLabel>
                    <Input
                      type="number"
                      value={validityDays}
                      onChange={(e) => setValidityDays(e.target.value)}
                      className={cn(FIELD_INPUT, 'px-2.5 text-sm')}
                    />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <FieldLabel>Tipo de documento</FieldLabel>
                  <Select value={documentType} onValueChange={setDocumentType}>
                    <SelectTrigger className={FIELD_INPUT}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="max-h-80">
                      {DOCUMENT_TYPE_GROUPS.map((group) => (
                        <SelectGroup key={group.label}>
                          <SelectLabel className="text-xs text-muted-foreground">{group.label}</SelectLabel>
                          {group.options.map((option) => (
                            <SelectItem key={option.value} value={option.value}>
                              {option.label}
                            </SelectItem>
                          ))}
                        </SelectGroup>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </AccordionContent>
            </AccordionItem>

            {/* --- pagamento e garantia */}
            <AccordionItem
              value="pagamento"
              className="overflow-hidden rounded-2xl border border-border/60 bg-card px-4"
            >
              <AccordionTrigger className="py-3.5 text-sm font-medium hover:no-underline">
                <span className="flex items-center gap-2">
                  <Hash className="h-4 w-4 text-primary" />
                  Pagamento e garantia
                </span>
              </AccordionTrigger>
              <AccordionContent className="space-y-4 pb-4">
                {/*
                  GARANTIA — número + unidade, com o TEXTO LIVRE preservado.
                  Documento antigo guarda a garantia numa frase ("1 ano na peça,
                  90 dias na mão de obra") que não cabe em número+unidade; ele
                  continua editando a frase e o app só OFERECE a conversão quando
                  o texto é inequívoco (`suggestWarranty`). Converter por parsing
                  em massa jogaria fora metade da informação, sem erro na tela.
                */}
                <div className="space-y-1.5">
                  <FieldLabel>Garantia</FieldLabel>
                  {warrantyAmount != null || (!warranty.trim() && !warrantyFreeMode) ? (
                    <div className="flex items-start gap-2">
                      <Input
                        inputMode="decimal"
                        placeholder="Ex: 90"
                        value={warrantyAmount ?? ''}
                        onChange={(e) => {
                          const parsed = Number(e.target.value.replace(',', '.'));
                          setWarrantyAmount(
                            e.target.value && Number.isFinite(parsed) && parsed > 0 ? parsed : null,
                          );
                        }}
                        className={cn(FIELD_INPUT, 'flex-1')}
                      />
                      <Select
                        value={warrantyUnit}
                        onValueChange={(v) => setWarrantyUnit(v as WarrantyUnit)}
                        disabled={warrantyAmount == null}
                      >
                        <SelectTrigger className={cn(FIELD_INPUT, 'w-32')}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {WARRANTY_UNITS.map((unit) => (
                            <SelectItem key={unit.value} value={unit.value}>
                              {unit.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  ) : (
                    <>
                      <Input
                        placeholder="Ex: 90 dias"
                        value={warranty}
                        onChange={(e) => setWarranty(e.target.value)}
                        className={FIELD_INPUT}
                      />
                      {(() => {
                        const suggestion = suggestWarranty(warranty);
                        if (!suggestion) return null;
                        return (
                          <button
                            type="button"
                            className="text-xs font-medium text-primary underline"
                            onClick={() => {
                              setWarrantyAmount(suggestion.warrantyAmount);
                              setWarrantyUnit(suggestion.warrantyUnit ?? 'days');
                              setWarranty('');
                              setWarrantyFreeMode(false);
                            }}
                          >
                            Usar como{' '}
                            {formatStructuredWarranty(
                              suggestion.warrantyAmount,
                              suggestion.warrantyUnit,
                            )}{' '}
                            (permite filtrar e acompanhar o prazo)
                          </button>
                        );
                      })()}
                    </>
                  )}
                  {warrantyAmount == null && (
                    <button
                      type="button"
                      className="text-[11px] text-muted-foreground underline"
                      onClick={() => setWarrantyFreeMode((v) => !v)}
                    >
                      {warrantyFreeMode
                        ? 'Voltar para número + unidade'
                        : 'Escrever a garantia por extenso'}
                    </button>
                  )}
                </div>

                <div className="space-y-2">
                  <FieldLabel>Formas de pagamento aceitas</FieldLabel>
                  <div className="flex flex-wrap gap-2">
                    {QUOTE_PAYMENT_METHODS.map((method) => {
                      const checked = paymentMethods.includes(method);
                      return (
                        <button
                          key={method}
                          type="button"
                          onClick={() => togglePaymentMethod(method)}
                          className={cn(
                            'rounded-full border px-3 py-1.5 text-xs font-medium transition-colors',
                            checked
                              ? 'border-primary bg-primary text-primary-foreground'
                              : 'border-input bg-background text-muted-foreground hover:bg-accent',
                          )}
                        >
                          {PAYMENT_METHOD_LABELS[method]}
                        </button>
                      );
                    })}
                  </div>
                  {paymentMethods.length === 0 && (
                    <p className="text-[11px] text-muted-foreground">
                      Nenhuma forma selecionada: o documento não vai listar formas de pagamento nem o QR
                      Code do PIX.
                    </p>
                  )}
                </div>

                <div className="space-y-2">
                  <FieldLabel>Condições de pagamento</FieldLabel>
                  <Input
                    placeholder="Ex: 50% na entrada e 50% na entrega"
                    value={paymentTerms}
                    onChange={(e) => setPaymentTerms(e.target.value)}
                    className={FIELD_INPUT}
                  />
                  <div className="flex flex-wrap gap-1.5">
                    {PAYMENT_TERMS_SUGGESTIONS.map((suggestion) => (
                      <button
                        key={suggestion}
                        type="button"
                        onClick={() => setPaymentTerms(suggestion)}
                        className="rounded-full border border-input bg-background px-2.5 py-1 text-[11px] text-muted-foreground hover:bg-accent hover:text-accent-foreground"
                      >
                        {suggestion}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="space-y-1.5">
                  <FieldLabel>Referência / nº externo</FieldLabel>
                  <Input
                    placeholder="Ex: OC 4471, contrato 2026/12"
                    value={reference}
                    onChange={(e) => setReference(e.target.value)}
                    className={FIELD_INPUT}
                  />
                  <p className="text-[11px] text-muted-foreground">
                    Número do cliente (ordem de compra, contrato, chamado). A numeração do
                    Orçamigo continua sendo a do documento.
                  </p>
                </div>
              </AccordionContent>
            </AccordionItem>

            {/* --- textos */}
            <AccordionItem
              value="textos"
              className="overflow-hidden rounded-2xl border border-border/60 bg-card px-4"
            >
              <AccordionTrigger className="py-3.5 text-sm font-medium hover:no-underline">
                <span className="flex items-center gap-2">
                  <Pencil className="h-4 w-4 text-primary" />
                  Observações e termos
                </span>
              </AccordionTrigger>
              <AccordionContent className="space-y-3 pb-4">
                <div className="space-y-1.5">
                  <FieldLabel>Observações</FieldLabel>
                  <Textarea
                    placeholder="Notas adicionais..."
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    rows={3}
                    className="rounded-xl bg-background"
                  />
                </div>
                <div className="space-y-1.5">
                  <FieldLabel>Termos e condições / contrato</FieldLabel>
                  <Textarea
                    placeholder="Termos legais, condições de pagamento, cláusulas contratuais..."
                    value={terms}
                    onChange={(e) => setTerms(e.target.value)}
                    rows={4}
                    className="rounded-xl bg-background"
                  />
                </div>
              </AccordionContent>
            </AccordionItem>

            {/* --- modelo do PDF (PRO) */}
            <AccordionItem
              value="modelo"
              className="overflow-hidden rounded-2xl border border-border/60 bg-card px-4"
            >
              <AccordionTrigger className="py-3.5 text-sm font-medium hover:no-underline">
                <span className="flex items-center gap-2">
                  <FileText className="h-4 w-4 text-primary" />
                  Modelo do PDF
                  <span className="text-xs font-normal text-muted-foreground">
                    {PDF_TEMPLATES.find((t) => t.value === pdfTemplate)?.label}
                  </span>
                </span>
              </AccordionTrigger>
              <AccordionContent className="pb-4">
                {/* Grade de 3 por linha: a lista rolável escondia os modelos
                    seguintes atrás da borda e o usuário não tinha como saber que
                    existiam mais. Empilhado, todos aparecem de uma vez. */}
                <div className="mb-2 grid grid-cols-3 gap-2">
                  {PDF_TEMPLATES.map((tpl) => {
                    const active = tpl.value === pdfTemplate;
                    return (
                      <button
                        key={tpl.value}
                        type="button"
                        onClick={() =>
                          tpl.pro
                            ? toast.info('Protótipo: modelo PRO abriria a tela de assinatura.')
                            : setPdfTemplate(tpl.value)
                        }
                        className={cn(
                          'relative rounded-xl border-2 p-2 text-left transition-all',
                          active ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/40',
                        )}
                      >
                        {tpl.pro && (
                          <span className="absolute right-1 top-1">
                            <ProTag />
                          </span>
                        )}
                        <div className="mb-2 h-16 rounded-md bg-gradient-to-b from-muted to-muted/40 p-1.5">
                          <div className="mb-1 h-1.5 w-8 rounded bg-primary/60" />
                          <div className="mb-1 h-1 w-full rounded bg-foreground/10" />
                          <div className="mb-1 h-1 w-4/5 rounded bg-foreground/10" />
                          <div className="h-1 w-3/5 rounded bg-foreground/10" />
                        </div>
                        <span className="text-[11px] font-medium">{tpl.label}</span>
                      </button>
                    );
                  })}
                </div>
                <p className="text-[11px] text-muted-foreground">
                  Vale só para este documento. O padrão dos novos fica nas configurações da empresa.
                </p>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>

        {/* ================================================================== */}
        {/* FOTOS / ANEXOS GERAIS (PRO)                                         */}
        {/* ================================================================== */}
        <div className="mt-6">
          <SectionTitle icon={ImageIcon} aside={<ProTag />}>
            Fotos / anexos
          </SectionTitle>
          <div className="rounded-2xl border border-border/60 bg-card p-3">
            <div className="grid grid-cols-3 gap-2">
              {generalPhotos.map((photo, index) => (
                <div key={photo.id} className="relative">
                  {/* Clicar na foto abre título/descrição — no PDF é essa legenda
                      que explica o que a imagem mostra. */}
                  <button
                    type="button"
                    onClick={() => setEditingPhoto(photo)}
                    className="flex h-20 w-full flex-col items-center justify-center gap-1 rounded-xl bg-primary/10 px-1"
                  >
                    <ImageIcon className="h-5 w-5 text-primary" />
                    <span className="w-full truncate text-center text-[10px] text-muted-foreground">
                      {photo.title || 'Sem título'}
                    </span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setGeneralPhotos((prev) => prev.filter((_, i) => i !== index))}
                    className="absolute -right-1.5 -top-1.5 flex h-5 w-5 items-center justify-center rounded-full bg-destructive text-white"
                    aria-label="Remover foto"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </div>
              ))}
              <button
                type="button"
                onClick={() => {
                  const photo = { id: createUuid(), title: '', description: '' };
                  setGeneralPhotos((prev) => [...prev, photo]);
                  setEditingPhoto(photo);
                }}
                className="flex h-20 flex-col items-center justify-center gap-1 rounded-xl border-2 border-dashed border-primary/40 text-[11px] text-primary hover:bg-primary/5"
              >
                <Camera className="h-5 w-5" />
                Adicionar
              </button>
            </div>
            {generalPhotos.length === 0 && (
              <p className="mt-2 text-[11px] text-muted-foreground">
                Fotos gerais do projeto ou da vistoria — entram no PDF depois dos itens.
              </p>
            )}
          </div>
        </div>

        {/* ================================================================== */}
        {/* AÇÕES — barra grudada acima da navegação inferior                   */}
        {/* ================================================================== */}
        {/* Sem internet, Link e Visualização não existem — o aviso precisa dizer
            o que AINDA funciona (salvar e gerar PDF), senão o usuário acha que
            perdeu o trabalho. */}
        {!isOnline && (
          <div className="mt-6 flex items-start gap-2 rounded-2xl border border-amber-500/50 bg-amber-500/10 p-3 text-xs text-amber-700 dark:text-amber-400">
            <WifiOff className="mt-0.5 h-4 w-4 shrink-0" />
            <span>
              Sem internet: Link e Visualização indisponíveis. Você pode salvar o orçamento e
              baixar o PDF offline.
            </span>
          </div>
        )}

        <div className="sticky bottom-[calc(var(--bottom-nav-h)+0.5rem)] z-20 mt-6 rounded-2xl border border-border/60 bg-card/95 p-3 shadow-[0_-6px_20px_-10px_rgba(0,0,0,0.35)] backdrop-blur">
          <div className="flex gap-2">
            <Button3D
              className="flex-1"
              onClick={() => toast.info('Protótipo: geraria o PDF do documento.')}
            >
              <Download className="mr-2 h-5 w-5" /> Baixar PDF
            </Button3D>
            <Button3D className="flex-1" onClick={handleSave} disabled={!!savedId}>
              <Save className="mr-2 h-5 w-5" /> {savedId ? 'Salvo ✓' : 'Salvar'}
            </Button3D>
          </div>
          <div className="mt-2 flex gap-2">
            <Button3D
              color="gray"
              size="sm"
              className="flex-1"
              disabled={!savedId || !isOnline}
              onClick={() => toast.success('Link copiado!')}
            >
              <Link2 className="mr-2 h-4 w-4" /> Copiar link
            </Button3D>
            <Button3D
              color="gray"
              size="sm"
              className="flex-1"
              disabled={!savedId || !isOnline}
              onClick={() => toast.info('Protótipo: abriria a página pública do orçamento.')}
            >
              <Eye className="mr-2 h-4 w-4" /> Visualizar
            </Button3D>
          </div>
          {!savedId && (
            <p className="mt-2 text-center text-[11px] text-muted-foreground">
              Link e visualização ficam disponíveis depois de salvar.
            </p>
          )}
        </div>
      </div>

      {/* ==================================================================== */}
      {/* Diálogos                                                              */}
      {/* ==================================================================== */}
      <Dialog open={clientSelectorOpen} onOpenChange={setClientSelectorOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Meus clientes</DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            {MOCK_CLIENTS.map((c) => (
              <button
                key={c.id}
                type="button"
                onClick={() => {
                  setClient(c);
                  setClientName(c.name);
                  setClientSelectorOpen(false);
                }}
                className="flex w-full items-center gap-3 rounded-xl border border-border/60 p-3 text-left hover:bg-accent"
              >
                <span className="flex h-9 w-9 items-center justify-center rounded-full bg-primary/10">
                  <User className="h-4 w-4 text-primary" />
                </span>
                <span className="min-w-0">
                  <span className="block truncate text-sm font-medium">{c.name}</span>
                  <span className="block truncate text-xs text-muted-foreground">{c.phone}</span>
                </span>
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!editingPhoto} onOpenChange={(open) => !open && setEditingPhoto(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Foto do documento</DialogTitle>
          </DialogHeader>
          {editingPhoto && (
            <div className="space-y-3">
              <div className="space-y-1.5">
                <FieldLabel>Título (opcional)</FieldLabel>
                <Input
                  placeholder="Ex: Vista frontal"
                  value={editingPhoto.title}
                  onChange={(e) => {
                    const title = e.target.value;
                    setEditingPhoto({ ...editingPhoto, title });
                    setGeneralPhotos((prev) =>
                      prev.map((p) => (p.id === editingPhoto.id ? { ...p, title } : p)),
                    );
                  }}
                  className={FIELD_INPUT}
                />
              </div>
              <div className="space-y-1.5">
                <FieldLabel>Descrição (opcional)</FieldLabel>
                <Textarea
                  placeholder="Ex: Estado atual da fachada..."
                  value={editingPhoto.description}
                  onChange={(e) => {
                    const description = e.target.value;
                    setEditingPhoto({ ...editingPhoto, description });
                    setGeneralPhotos((prev) =>
                      prev.map((p) => (p.id === editingPhoto.id ? { ...p, description } : p)),
                    );
                  }}
                  rows={3}
                  className="rounded-xl bg-background"
                />
              </div>
              <Button3D className="w-full" onClick={() => setEditingPhoto(null)}>
                Concluir
              </Button3D>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={catalogOpen} onOpenChange={setCatalogOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Do catálogo</DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            {MOCK_CATALOG.map((entry) => (
              <button
                key={entry.name}
                type="button"
                onClick={() => addFromCatalog(entry)}
                className="flex w-full items-center gap-3 rounded-xl border border-border/60 p-3 text-left hover:bg-accent"
              >
                <span
                  className={cn(
                    'flex h-9 w-9 items-center justify-center rounded-full',
                    entry.type === 'product'
                      ? 'bg-amber-500/15 text-amber-600'
                      : 'bg-[#2f6fae]/15 text-[#2f6fae]',
                  )}
                >
                  {entry.type === 'product' ? (
                    <ShoppingBag className="h-4 w-4" />
                  ) : (
                    <Wrench className="h-4 w-4" />
                  )}
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-medium">{entry.name}</span>
                  <span className="block text-xs text-muted-foreground">
                    {formatCurrency(entry.price)}
                  </span>
                </span>
                <Plus className="h-4 w-4 shrink-0 text-primary" />
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </MainLayout>
  );
}
